import useApiCall, {makeApiCall} from "../../hooks/CancellableApiCall";
import {LoadingContent} from "../LoadingContent";
import React, {useMemo, useState} from "react";
import {ProfileResponse} from "../../data/ProfileResponse";
import {ConversationMessageResponse, ConversationUserSummary} from "../../data/ConversationResponse";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Pagination,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import {formatTimestamp} from "../../utils";
import LockIcon from "@mui/icons-material/Lock";

interface RecordInboxConversationResponse {
    id: number
    title: string
    createdAt: string
    lastMessageDate?: string
    creator: ConversationUserSummary
    lastPoster?: ConversationUserSummary
    replyCount: number
    participantCount: number
    locked: boolean
}

interface RecordInboxPaginatedResponse {
    totalPages: number
    totalConversations: number
    conversations: RecordInboxConversationResponse[]
}

interface EmployeeInboxProps {
    employee: ProfileResponse
    session?: string
}

export const EmployeeInbox = (props: EmployeeInboxProps) => {
    const [page, setPage] = useState(1)
    const [searchInput, setSearchInput] = useState("")
    const [query, setQuery] = useState("")
    const [createdOnly, setCreatedOnly] = useState(false)
    const [openConversationId, setOpenConversationId] = useState<number | null>(null)
    const [messages, setMessages] = useState<ConversationMessageResponse[] | null>(null)
    const [loadingMessages, setLoadingMessages] = useState(false)
    const [openTitle, setOpenTitle] = useState("")

    const url = useMemo(() => {
        const params = new URLSearchParams()
        params.set("page", String(page))
        if (query) params.set("query", query)
        if (createdOnly) params.set("createdOnly", "true")
        if (props.session) params.set("session", props.session)
        return `/api/records/inbox/${props.employee.communityId}?${params.toString()}`
    }, [page, query, createdOnly, props.employee.communityId, props.session])

    const loadInbox = useApiCall<RecordInboxPaginatedResponse>({url})

    function handleSearch() {
        setQuery(searchInput)
        setPage(1)
    }

    function handleKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Enter") handleSearch()
    }

    function handleCreatedOnlyChange(checked: boolean) {
        setCreatedOnly(checked)
        setPage(1)
    }

    function handleOpenConversation(conversation: RecordInboxConversationResponse) {
        setOpenConversationId(conversation.id)
        setOpenTitle(conversation.title)
        setLoadingMessages(true)
        const openSessionParam = props.session ? `?session=${props.session}` : ""
        makeApiCall<ConversationMessageResponse[]>({
            url: `/api/records/inbox/${props.employee.communityId}/open/${conversation.id}${openSessionParam}`,
            method: "POST",
            onLoadedCallback: (data) => {
                setMessages(data)
                setLoadingMessages(false)
            },
            onError: () => {
                setLoadingMessages(false)
            }
        })
    }

    function handleClose() {
        setOpenConversationId(null)
        setMessages(null)
        setOpenTitle("")
    }

    const totalPages = Math.max(1, loadInbox.data?.totalPages ?? 1)
    const conversations = loadInbox.data?.conversations ?? []

    return <>
        <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: "wrap"}}>
                <TextField
                    label="Search by title"
                    variant="outlined"
                    size="small"
                    sx={{minWidth: 240}}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <Button variant="outlined" size="small" onClick={handleSearch}>Search</Button>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={createdOnly}
                            onChange={(_, checked) => handleCreatedOnlyChange(checked)}
                            size="small"
                        />
                    }
                    label="Only show created"
                />
                {(query || createdOnly) && (
                    <Button size="small" color="warning" onClick={() => { setQuery(""); setSearchInput(""); setCreatedOnly(false); setPage(1) }}>
                        Clear
                    </Button>
                )}
            </Stack>

            <LoadingContent isLoading={loadInbox.isLoading}>
                {conversations.length === 0 ? (
                    <Typography variant="body1" color="text.secondary">No conversations found.</Typography>
                ) : (
                    <Stack spacing={2}>
                        <Box sx={{overflowX: "auto"}}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Title</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell>Last Activity</TableCell>
                                        <TableCell>Creator</TableCell>
                                        <TableCell>Replies</TableCell>
                                        <TableCell>Participants</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {conversations.map((conversation) => (
                                        <TableRow key={conversation.id}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="body2">{conversation.title}</Typography>
                                                    {conversation.locked && <LockIcon fontSize="small" color="disabled"/>}
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{whiteSpace: "nowrap"}}>{formatTimestamp(conversation.createdAt)}</TableCell>
                                            <TableCell sx={{whiteSpace: "nowrap"}}>{conversation.lastMessageDate ? formatTimestamp(conversation.lastMessageDate) : "—"}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={conversation.creator.nick}/>
                                            </TableCell>
                                            <TableCell>{conversation.replyCount}</TableCell>
                                            <TableCell>{conversation.participantCount}</TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="warning"
                                                    onClick={() => handleOpenConversation(conversation)}
                                                >
                                                    Open
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                        <Box sx={{display: "flex", justifyContent: "center"}}>
                            <Pagination
                                color="secondary"
                                count={totalPages}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                            />
                        </Box>
                    </Stack>
                )}
            </LoadingContent>
        </Stack>

        <Dialog open={openConversationId !== null} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{openTitle}</DialogTitle>
            <DialogContent>
                {loadingMessages && <Typography>Loading...</Typography>}
                {messages && (
                    <Stack spacing={2} sx={{mt: 1}}>
                        {messages.map((msg) => (
                            <Box key={msg.id} sx={{p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1}}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 0.5}}>
                                    <Typography variant="subtitle2">{msg.user.nick}</Typography>
                                    <Typography variant="caption" color="text.secondary">{formatTimestamp(msg.date)}</Typography>
                                </Stack>
                                <Typography variant="body2" dangerouslySetInnerHTML={{__html: msg.content}}/>
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    </>
}
